import { GetTeamResponseType } from '@distribute/shared/api-types/team';
import { api } from './instance';
import {
  RegenerateApiKeyRequest,
  RegenerateApiKeyResponse,
} from '@distribute/shared/api-types/zapier';
import {
  TeamHubspotSettings,
  TeamSalesforceSettings,
} from '@distribute/shared/types';

type CreateLinkedinPostProps = {
  requestData: FormData;
  text: string;
  title: string;
  contentLandingPage: string;
  userId: string;
};

type CreateTwitterPostProps = {
  requestData: FormData;
  text: string;
};

export const integrationsApi = {
  async regenerateApiKey(data: RegenerateApiKeyRequest) {
    const { data: responseData } = await api.post<RegenerateApiKeyResponse>(
      'zapier/regenerate-api-key',
      data
    );

    return responseData;
  },

  async getLinkedinAccessToken({
    code,
    redirectUri,
    clientId,
  }: {
    code: string;
    redirectUri: string;
    clientId: string;
  }) {
    const { data } = await api.get('integrations/linkedin/access-token', {
      params: {
        code,
        clientId,
        redirectUri,
      },
    });
    return data;
  },

  async getLinkedinProfileData(token: string) {
    const { data } = await api.get('integrations/linkedin/profile', {
      headers: { 'linkedin-token': token },
    });
    return data;
  },

  async createLinkedinPost(
    token: string,
    {
      requestData,
      title,
      text,
      userId,
      contentLandingPage,
    }: CreateLinkedinPostProps
  ) {
    const { data } = await api.post('integrations/linkedin/post', requestData, {
      headers: { 'linkedin-token': token },
      params: {
        text,
        userId,
        contentLandingPage,
        title,
      },
    });
    return data;
  },

  async revokeLinkedinToken(token: string, clientId: string) {
    await api.post('integrations/linkedin/logout', null, {
      headers: { 'linkedin-token': token },
      params: {
        clientId,
      },
    });
  },

  async getTwitterAuthLink(callbackUrl: string) {
    const { data } = await api.get('integrations/twitter/link', {
      params: {
        callbackUrl,
      },
    });
    return data;
  },

  async getTwitterAccessToken(
    oauth_token: string,
    oauth_verifier: string,
    oauth_token_secret: string
  ) {
    const { data } = await api.get('integrations/twitter/token', {
      params: {
        oauth_token,
        oauth_verifier,
        oauth_token_secret,
      },
    });
    return data;
  },

  async createTwitterPost(
    token: string,
    tokenSecret: string,
    { requestData, text }: CreateTwitterPostProps
  ) {
    const { data } = await api.post('integrations/twitter/post', requestData, {
      headers: { 'twitter-token': token, 'twitter-token-secret': tokenSecret },
      params: {
        text,
      },
    });
    return data;
  },

  async getTwitterProfileData(token: string, tokenSecret: string) {
    const { data } = await api.get('integrations/twitter/profile', {
      headers: { 'twitter-token': token, 'twitter-token-secret': tokenSecret },
    });
    return data;
  },

  async revokeTwitterToken(token: string, tokenSecret: string) {
    await api.post('integrations/twitter/logout', null, {
      headers: { 'twitter-token': token, 'twitter-token-secret': tokenSecret },
    });
  },

  async getSalesforcePkceParameters(teamId: number) {
    const { data } = await api.get(`integrations/salesforce/pkce/${teamId}`);
    return data;
  },

  async requestSalesforceAccessToken({
    code,
    redirectUri,
    clientId,
    codeVerifier,
    teamId,
  }: {
    code: string;
    redirectUri: string;
    clientId: string;
    codeVerifier: string;
    teamId: number;
  }) {
    const { data } = await api.post<GetTeamResponseType>(
      `integrations/salesforce/access-token/${teamId}`,
      {
        code,
        clientId,
        redirectUri,
        codeVerifier: codeVerifier,
      }
    );
    return data;
  },

  async revokeSalesforceAccessToken({ teamId }: { teamId: number }) {
    const { data } = await api.delete<GetTeamResponseType>(
      `integrations/salesforce/access-token/${teamId}`
    );
    return data;
  },

  async updateSalesforceSettings({
    teamId,
    settings,
  }: {
    teamId: number;
    settings: TeamSalesforceSettings;
  }) {
    const { data } = await api.patch<GetTeamResponseType>(
      `integrations/salesforce/settings/${teamId}`,
      settings
    );
    return data;
  },

  async getCRMData(teamId: number, search = '') {
    const { data } = await api.get(`integrations/crm/objects/${teamId}`, {
      params: {
        search,
      },
    });
    return data;
  },

  async requestHubspotAccessToken({
    code,
    redirectUri,
    teamId,
  }: {
    code: string;
    redirectUri: string;
    teamId: number;
  }) {
    await api.post(`integrations/hubspot/access-token/${teamId}`, {
      code,
      redirectUri,
    });
  },

  async revokeHubspotAccessToken({ teamId }: { teamId: number }) {
    await api.delete(`integrations/hubspot/access-token/${teamId}`);
  },

  async updateHubspotSettings({
    teamId,
    settings,
  }: {
    teamId: number;
    settings: TeamHubspotSettings;
  }) {
    const { data } = await api.patch<GetTeamResponseType>(
      `integrations/hubspot/settings/${teamId}`,
      settings
    );
    return data;
  },
};
